import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SaveIcon from "@material-ui/icons/Save";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const emptyPlan = {
  name: "",
  priceLabel: "",
  paymentUrl: "",
  buttonLabel: "",
  description: "",
  active: true,
  highlighted: false,
};

const emptyDomain = {
  domain: "",
};

const emptyLink = {
  label: "",
  url: "",
};

const emptySlide = {
  imageUrl: "",
  title: "",
  description: "",
};

const emptyLandingSection = {
  title: "",
  description: "",
  markerStyle: "numbers",
  items: [{ title: "", description: "" }],
};

const emptyLandingSectionItem = {
  title: "",
  description: "",
};

const emptyCompany = {
  companyName: "",
  name: "",
  email: "",
  password: "",
};

const renewalOptions = [
  { value: 1, label: "1 mes", chargedMonths: 1, grantedMonths: 1 },
  { value: 3, label: "3 meses", chargedMonths: 3, grantedMonths: 3 },
  { value: 6, label: "6 meses + 1 gratis", chargedMonths: 6, grantedMonths: 7 },
  { value: 12, label: "12 meses + 2 gratis", chargedMonths: 12, grantedMonths: 14 },
];

const emptyReseller = {
  name: "",
  slug: "",
  status: "active",
  landingTemplate: "service-profile",
  landingHeroHeight: 52,
  brandName: "",
  headline: "",
  subheadline: "",
  logoUrl: "",
  primaryColor: "#1976d2",
  accentColor: "#10b981",
  whatsappNumber: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  loginLinks: [],
  loginSlides: [],
  landingSections: [],
  plans: [],
  domains: [],
  companies: [],
  creditBalance: 0,
};

const slugify = value =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const formatDate = value => {
  if (!value) return "Sin vencimiento";
  return new Date(value).toLocaleDateString();
};

const toDateInputValue = value => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
};

const dateInputToExpiration = value => {
  if (!value) return null;
  return `${value}T23:59:59.999Z`;
};

const getRemainingDays = value => {
  if (!value) return null;

  const expiresAt = new Date(value);
  if (Number.isNaN(expiresAt.getTime())) return null;

  return Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
};

const formatRemainingDays = days => {
  if (days === null) return "Sin activar";
  if (days < 0) return `Vencida hace ${Math.abs(days)} dias`;
  if (days === 0) return "Vence hoy";
  if (days === 1) return "Queda 1 dia";
  return `Quedan ${days} dias`;
};

const getMenuPermissions = user => {
  const permissions = user?.company?.menuPermissions;

  if (Array.isArray(permissions)) return permissions;

  if (typeof permissions === "string" && permissions.trim()) {
    try {
      const parsed = JSON.parse(permissions);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

const useStyles = makeStyles(theme => ({
  layout: {
    display: "grid",
    gridTemplateColumns: "300px minmax(0, 1fr)",
    gap: theme.spacing(2),
    minHeight: 0,
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  singleLayout: {
    gridTemplateColumns: "1fr",
  },
  panel: {
    padding: theme.spacing(2),
    overflow: "auto",
  },
  listButton: {
    justifyContent: "flex-start",
    marginBottom: theme.spacing(1),
    textAlign: "left",
    width: "100%",
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  section: {
    marginTop: theme.spacing(2),
  },
  planRow: {
    display: "grid",
    gridTemplateColumns: "1fr 0.7fr 1.1fr 0.9fr 1.1fr 120px 120px auto",
    gap: theme.spacing(1),
    alignItems: "center",
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  domainRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: theme.spacing(1),
    alignItems: "center",
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  linkRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1.6fr auto",
    gap: theme.spacing(1),
    alignItems: "center",
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  slideRow: {
    display: "grid",
    gridTemplateColumns: "1.4fr 1fr 1.4fr auto",
    gap: theme.spacing(1),
    alignItems: "center",
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  sectionBlock: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  sectionHeaderRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 150px auto",
    gap: theme.spacing(1),
    alignItems: "center",
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  sectionItemRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1.4fr auto",
    gap: theme.spacing(1),
    alignItems: "center",
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  companyList: {
    display: "grid",
    gap: theme.spacing(1),
  },
  companyItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
  },
  companyActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  connectionItem: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

const Reseller = () => {
  const classes = useStyles();
  const { user, handleImpersonate } = useContext(AuthContext);
  const isSuperAdmin = user?.profile === "superadmin";
  const hasResellerSection = getMenuPermissions(user).includes("reseller");
  const canUseReseller = isSuperAdmin || hasResellerSection;
  const [resellers, setResellers] = useState([]);
  const [selectedResellerId, setSelectedResellerId] = useState("");
  const [form, setForm] = useState(emptyReseller);
  const [tab, setTab] = useState("brand");
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [creditAmount, setCreditAmount] = useState(1);
  const [creditNote, setCreditNote] = useState("");
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [companyForm, setCompanyForm] = useState(emptyCompany);
  const [connectionDialogOpen, setConnectionDialogOpen] = useState(false);
  const [connectionQuantity, setConnectionQuantity] = useState(1);
  const [selectedCompanyForConnections, setSelectedCompanyForConnections] = useState(null);
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [renewTarget, setRenewTarget] = useState(null);
  const [renewalMonths, setRenewalMonths] = useState(1);
  const [expirationDialogOpen, setExpirationDialogOpen] = useState(false);
  const [expirationTarget, setExpirationTarget] = useState(null);
  const [expirationDate, setExpirationDate] = useState("");

  const selectedReseller = useMemo(
    () => resellers.find(item => String(item.id) === String(selectedResellerId)),
    [resellers, selectedResellerId]
  );
  const selectedRenewalOption = renewalOptions.find(
    option => Number(option.value) === Number(renewalMonths)
  ) || renewalOptions[0];

  const loadResellers = async () => {
    if (isSuperAdmin) {
      const { data } = await api.get("/superadmin/resellers");
      const items = data.resellers || [];
      setResellers(items);
      setSelectedResellerId(current =>
        items.some(item => String(item.id) === String(current))
          ? current
          : items[0]?.id || ""
      );
      return;
    }

    const { data } = await api.get("/reseller/overview");
    setResellers([data]);
    setSelectedResellerId(data.id);
  };

  useEffect(() => {
    if (!canUseReseller) return;
    loadResellers().catch(toastError);
  }, [canUseReseller, isSuperAdmin]);

  useEffect(() => {
    if (!selectedReseller) {
      setForm(emptyReseller);
      return;
    }

    setForm({
      ...emptyReseller,
      ...selectedReseller,
      plans: selectedReseller.plans || [],
      domains: selectedReseller.domains || [],
      loginLinks: selectedReseller.loginLinks || [],
      loginSlides: selectedReseller.loginSlides || [],
      landingSections: selectedReseller.landingSections || [],
      companies: selectedReseller.companies || [],
    });
  }, [selectedReseller]);

  const updateField = (field, value) => {
    setForm(current => ({
      ...current,
      [field]: value,
      ...(field === "name" && !current.slug ? { slug: slugify(value) } : {}),
    }));
  };

  const updatePlan = (index, field, value) => {
    setForm(current => ({
      ...current,
      plans: current.plans.map((plan, planIndex) =>
        planIndex === index
          ? { ...plan, [field]: value }
          : field === "highlighted" && value === true
            ? { ...plan, highlighted: false }
            : plan
      ),
    }));
  };

  const updateDomain = (index, field, value) => {
    setForm(current => ({
      ...current,
      domains: current.domains.map((domain, domainIndex) =>
        domainIndex === index ? { ...domain, [field]: value } : domain
      ),
    }));
  };

  const updateLink = (index, field, value) => {
    setForm(current => ({
      ...current,
      loginLinks: current.loginLinks.map((link, linkIndex) =>
        linkIndex === index ? { ...link, [field]: value } : link
      ),
    }));
  };

  const updateSlide = (index, field, value) => {
    setForm(current => ({
      ...current,
      loginSlides: current.loginSlides.map((slide, slideIndex) =>
        slideIndex === index ? { ...slide, [field]: value } : slide
      ),
    }));
  };

  const updateLandingSection = (index, field, value) => {
    setForm(current => ({
      ...current,
      landingSections: current.landingSections.map((section, sectionIndex) =>
        sectionIndex === index ? { ...section, [field]: value } : section
      ),
    }));
  };

  const updateLandingSectionItem = (sectionIndex, itemIndex, field, value) => {
    setForm(current => ({
      ...current,
      landingSections: current.landingSections.map((section, currentSectionIndex) =>
        currentSectionIndex === sectionIndex
          ? {
              ...section,
              items: (section.items || []).map((item, currentItemIndex) =>
                currentItemIndex === itemIndex
                  ? {
                      ...(typeof item === "string" ? { title: item, description: "" } : item),
                      [field]: value,
                    }
                  : item
              ),
            }
          : section
      ),
    }));
  };

  const handleSave = async event => {
    event.preventDefault();

    try {
      const payload = {
        ...form,
        slug: slugify(form.slug || form.name),
        plans: form.plans.map((plan, index) => ({ ...plan, position: index })),
      };

      const { data } = isSuperAdmin
        ? await api.put(`/superadmin/resellers/${form.id}`, payload)
        : await api.put("/reseller/overview", payload);

      toast.success("Reseller guardado");
      await loadResellers();
      setSelectedResellerId(data.id);
    } catch (err) {
      toastError(err);
    }
  };

  const handleAddCredits = async event => {
    event.preventDefault();
    if (!selectedReseller?.id) return;

    try {
      await api.post(`/superadmin/resellers/${selectedReseller.id}/credits`, {
        amount: Number(creditAmount),
        note: creditNote,
      });
      toast.success("Creditos actualizados");
      setCreditDialogOpen(false);
      setCreditAmount(1);
      setCreditNote("");
      await loadResellers();
    } catch (err) {
      toastError(err);
    }
  };

  const handleCreateCompany = async event => {
    event.preventDefault();

    try {
      await api.post("/reseller/companies", {
        ...companyForm,
        email: companyForm.email.trim(),
        name: companyForm.name.trim(),
        companyName: companyForm.companyName.trim(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      });
      toast.success("Cuenta creada");
      setCompanyDialogOpen(false);
      setCompanyForm(emptyCompany);
      await loadResellers();
    } catch (err) {
      toastError(err);
    }
  };

  const openConnectionDialog = company => {
    setSelectedCompanyForConnections(company);
    setConnectionQuantity(1);
    setConnectionDialogOpen(true);
  };

  const handleAddConnections = async event => {
    event.preventDefault();

    if (!selectedCompanyForConnections?.id) return;

    try {
      await api.post(`/reseller/companies/${selectedCompanyForConnections.id}/connections`, {
        quantity: Number(connectionQuantity),
      });
      toast.success("Conexiones agregadas");
      setConnectionDialogOpen(false);
      setSelectedCompanyForConnections(null);
      setConnectionQuantity(1);
      await loadResellers();
    } catch (err) {
      toastError(err);
    }
  };

  const handleRenewConnection = (company, connection) => {
    setRenewTarget({ company, connection });
    setRenewalMonths(1);
    setRenewDialogOpen(true);
  };

  const confirmRenewConnection = async () => {
    if (!renewTarget?.company?.id || !renewTarget?.connection?.id) return;

    try {
      await api.post(`/reseller/companies/${renewTarget.company.id}/renew`, {
        months: Number(renewalMonths),
        connectionId: renewTarget.connection.id,
      });
      toast.success("Conexion renovada");
      setRenewDialogOpen(false);
      setRenewTarget(null);
      await loadResellers();
    } catch (err) {
      toastError(err);
    }
  };

  const handleEditConnectionExpiration = (company, connection) => {
    setExpirationTarget({ company, connection });
    setExpirationDate(toDateInputValue(connection.expiresAt));
    setExpirationDialogOpen(true);
  };

  const closeExpirationDialog = () => {
    setExpirationDialogOpen(false);
    setExpirationTarget(null);
    setExpirationDate("");
  };

  const confirmUpdateConnectionExpiration = async () => {
    if (!expirationTarget?.company?.id || !expirationTarget?.connection?.id) return;

    try {
      const { data } = await api.put(
        `/superadmin/reseller-companies/${expirationTarget.company.id}/connections/${expirationTarget.connection.id}`,
        {
          expiresAt: dateInputToExpiration(expirationDate),
        }
      );
      toast.success("Vencimiento actualizado");
      setResellers(current =>
        current.map(reseller =>
          String(reseller.id) === String(data.id) ? data : reseller
        )
      );
      setForm(current => (String(current.id) === String(data.id) ? data : current));
      closeExpirationDialog();
    } catch (err) {
      toastError(err);
    }
  };

  const getCompanyAdminUser = company =>
    (company.users || []).find(accountUser => accountUser.profile === "admin") ||
    (company.users || [])[0] ||
    null;

  const handleEnterAccount = async company => {
    const targetUser = getCompanyAdminUser(company);

    if (!targetUser?.id) {
      toast.error("Esta cuenta no tiene usuario para ingresar");
      return;
    }

    try {
      await handleImpersonate(targetUser.id);
      toast.success(`Ingresando como ${targetUser.name}`);
    } catch (err) {
      toastError(err);
    }
  };

  if (!canUseReseller) {
    return (
      <MainContainer>
        <MainHeader>
          <Title>Reseller</Title>
        </MainHeader>
        <Paper className={classes.panel}>
          <Typography>No tienes permiso para ver esta seccion.</Typography>
        </Paper>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Reseller</Title>
        <MainHeaderButtonsWrapper>
          {!isSuperAdmin && (
            <Button
              color="primary"
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!form.id}
              onClick={() => setCompanyDialogOpen(true)}
            >
              Crear cuenta
            </Button>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className={`${classes.layout} ${!isSuperAdmin ? classes.singleLayout : ""}`}>
        {isSuperAdmin && (
          <Paper className={classes.panel} variant="outlined">
            {resellers.map(reseller => (
              <Button
                key={reseller.id}
                className={classes.listButton}
                color={String(reseller.id) === String(selectedResellerId) ? "primary" : "default"}
                variant={String(reseller.id) === String(selectedResellerId) ? "contained" : "outlined"}
                onClick={() => setSelectedResellerId(reseller.id)}
              >
                <span>
                  {reseller.name}
                  <br />
                  <small>{reseller.creditBalance || 0} creditos</small>
                </span>
              </Button>
            ))}
            {!resellers.length && (
              <Typography variant="body2" color="textSecondary">
                No hay cuentas con Reseller activo.
              </Typography>
            )}
          </Paper>
        )}

        <Paper className={classes.panel} variant="outlined">
          {!form.id ? (
            <Typography variant="body2" color="textSecondary">
              Activa la seccion Reseller desde Cuentas SaaS.
            </Typography>
          ) : (
            <form onSubmit={handleSave}>
              <div className={classes.actions}>
                <Chip color="primary" label={`${form.creditBalance || 0} creditos`} />
                {form.slug && (
                  <Chip
                    label={`/r/${form.slug}`}
                    component="a"
                    href={`/r/${form.slug}`}
                    clickable
                    target="_blank"
                  />
                )}
                {isSuperAdmin && (
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={() => setCreditDialogOpen(true)}
                  >
                    Agregar creditos
                  </Button>
                )}
                <Button color="primary" variant="contained" type="submit" startIcon={<SaveIcon />}>
                  Guardar
                </Button>
              </div>

              <Tabs value={tab} onChange={(event, value) => setTab(value)}>
                <Tab value="brand" label="Marca" />
                <Tab value="content" label="Landing" />
                <Tab value="plans" label="Planes" />
                <Tab value="domains" label="Dominios" />
                <Tab value="companies" label="Cuentas" />
              </Tabs>

              {tab === "brand" && (
                <Grid container spacing={2} className={classes.section}>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth variant="outlined" margin="dense" label="Nombre interno" value={form.name} onChange={event => updateField("name", event.target.value)} disabled />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth variant="outlined" margin="dense" label="Slug" value={form.slug} onChange={event => updateField("slug", slugify(event.target.value))} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined" margin="dense">
                      <InputLabel>Estado</InputLabel>
                      <Select value={form.status || "active"} label="Estado" onChange={event => updateField("status", event.target.value)} disabled={!isSuperAdmin}>
                        <MenuItem value="active">Activo</MenuItem>
                        <MenuItem value="inactive">Inactivo</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth variant="outlined" margin="dense" label="Marca publica" value={form.brandName} onChange={event => updateField("brandName", event.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth variant="outlined" margin="dense" label="WhatsApp publico" value={form.whatsappNumber} onChange={event => updateField("whatsappNumber", event.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth variant="outlined" margin="dense" label="Logo URL" value={form.logoUrl} onChange={event => updateField("logoUrl", event.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth variant="outlined" margin="dense" label="Titulo landing" value={form.headline} onChange={event => updateField("headline", event.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline minRows={3} variant="outlined" margin="dense" label="Descripcion landing" value={form.subheadline} onChange={event => updateField("subheadline", event.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth variant="outlined" margin="dense" label="Color primario" value={form.primaryColor} onChange={event => updateField("primaryColor", event.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth variant="outlined" margin="dense" label="Color acento" value={form.accentColor} onChange={event => updateField("accentColor", event.target.value)} />
                  </Grid>
                </Grid>
              )}

              {tab === "content" && (
                <div className={classes.section}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth variant="outlined" margin="dense">
                        <InputLabel>Plantilla</InputLabel>
                        <Select value={form.landingTemplate || "service-profile"} label="Plantilla" onChange={event => updateField("landingTemplate", event.target.value)}>
                          <MenuItem value="service-profile">Ficha tipo servicio</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth type="number" variant="outlined" margin="dense" label="Alto imagen (vh)" value={form.landingHeroHeight || 52} inputProps={{ min: 32, max: 78 }} onChange={event => updateField("landingHeroHeight", event.target.value)} />
                    </Grid>
                  </Grid>

                  <Typography variant="subtitle2">Enlaces</Typography>
                  {(form.loginLinks || []).map((link, index) => (
                    <div className={classes.linkRow} key={index}>
                      <TextField variant="outlined" margin="dense" label="Etiqueta" value={link.label} onChange={event => updateLink(index, "label", event.target.value)} />
                      <TextField variant="outlined" margin="dense" label="URL" value={link.url} onChange={event => updateLink(index, "url", event.target.value)} />
                      <Button onClick={() => setForm(current => ({ ...current, loginLinks: current.loginLinks.filter((item, linkIndex) => linkIndex !== index) }))}>
                        <DeleteOutlineIcon />
                      </Button>
                    </div>
                  ))}
                  <Button startIcon={<AddIcon />} onClick={() => setForm(current => ({ ...current, loginLinks: [...current.loginLinks, emptyLink] }))}>
                    Agregar enlace
                  </Button>

                  <Typography className={classes.section} variant="subtitle2">Slides</Typography>
                  {(form.loginSlides || []).map((slide, index) => (
                    <div className={classes.slideRow} key={index}>
                      <TextField variant="outlined" margin="dense" label="URL de imagen" value={slide.imageUrl} onChange={event => updateSlide(index, "imageUrl", event.target.value)} />
                      <TextField variant="outlined" margin="dense" label="Titulo" value={slide.title} onChange={event => updateSlide(index, "title", event.target.value)} />
                      <TextField variant="outlined" margin="dense" label="Descripcion" value={slide.description} onChange={event => updateSlide(index, "description", event.target.value)} />
                      <Button onClick={() => setForm(current => ({ ...current, loginSlides: current.loginSlides.filter((item, slideIndex) => slideIndex !== index) }))}>
                        <DeleteOutlineIcon />
                      </Button>
                    </div>
                  ))}
                  <Button startIcon={<AddIcon />} onClick={() => setForm(current => ({ ...current, loginSlides: [...current.loginSlides, emptySlide] }))}>
                    Agregar slide
                  </Button>

                  <Typography className={classes.section} variant="subtitle2">Secciones inferiores</Typography>
                  {(form.landingSections || []).map((section, sectionIndex) => (
                    <div className={classes.sectionBlock} key={sectionIndex}>
                      <div className={classes.sectionHeaderRow}>
                        <TextField variant="outlined" margin="dense" label="Titulo" value={section.title} onChange={event => updateLandingSection(sectionIndex, "title", event.target.value)} />
                        <TextField variant="outlined" margin="dense" label="Descripcion" value={section.description} onChange={event => updateLandingSection(sectionIndex, "description", event.target.value)} />
                        <FormControl variant="outlined" margin="dense">
                          <InputLabel>Marcador</InputLabel>
                          <Select value={section.markerStyle || "numbers"} label="Marcador" onChange={event => updateLandingSection(sectionIndex, "markerStyle", event.target.value)}>
                            <MenuItem value="numbers">Numeros</MenuItem>
                            <MenuItem value="points">Puntos</MenuItem>
                          </Select>
                        </FormControl>
                        <Button onClick={() => setForm(current => ({ ...current, landingSections: current.landingSections.filter((item, currentSectionIndex) => currentSectionIndex !== sectionIndex) }))}>
                          <DeleteOutlineIcon />
                        </Button>
                      </div>
                      {(section.items || []).map((item, itemIndex) => (
                        <div className={classes.sectionItemRow} key={itemIndex}>
                          <TextField variant="outlined" margin="dense" label={`Titulo item ${itemIndex + 1}`} value={typeof item === "string" ? item : item.title || ""} onChange={event => updateLandingSectionItem(sectionIndex, itemIndex, "title", event.target.value)} />
                          <TextField variant="outlined" margin="dense" label="Descripcion item" value={typeof item === "string" ? "" : item.description || ""} onChange={event => updateLandingSectionItem(sectionIndex, itemIndex, "description", event.target.value)} />
                          <Button onClick={() => setForm(current => ({ ...current, landingSections: current.landingSections.map((currentSection, currentSectionIndex) => currentSectionIndex === sectionIndex ? { ...currentSection, items: (currentSection.items || []).filter((currentItem, currentItemIndex) => currentItemIndex !== itemIndex) } : currentSection) }))}>
                            <DeleteOutlineIcon />
                          </Button>
                        </div>
                      ))}
                      <Button size="small" startIcon={<AddIcon />} onClick={() => setForm(current => ({ ...current, landingSections: current.landingSections.map((currentSection, currentSectionIndex) => currentSectionIndex === sectionIndex ? { ...currentSection, items: [...(currentSection.items || []), emptyLandingSectionItem] } : currentSection) }))}>
                        Agregar item
                      </Button>
                    </div>
                  ))}
                  <Button startIcon={<AddIcon />} onClick={() => setForm(current => ({ ...current, landingSections: [...current.landingSections, emptyLandingSection] }))}>
                    Agregar seccion
                  </Button>
                </div>
              )}

              {tab === "plans" && (
                <div className={classes.section}>
                  {form.plans.map((plan, index) => (
                    <div className={classes.planRow} key={plan.id || index}>
                      <TextField variant="outlined" margin="dense" label="Plan" value={plan.name} onChange={event => updatePlan(index, "name", event.target.value)} />
                      <TextField variant="outlined" margin="dense" label="Precio" value={plan.priceLabel} onChange={event => updatePlan(index, "priceLabel", event.target.value)} />
                      <TextField variant="outlined" margin="dense" label="Link pago" value={plan.paymentUrl} onChange={event => updatePlan(index, "paymentUrl", event.target.value)} />
                      <TextField variant="outlined" margin="dense" label="Texto boton" value={plan.buttonLabel || ""} onChange={event => updatePlan(index, "buttonLabel", event.target.value)} />
                      <TextField variant="outlined" margin="dense" label="Descripcion" value={plan.description} onChange={event => updatePlan(index, "description", event.target.value)} />
                      <FormControl variant="outlined" margin="dense">
                        <InputLabel>Estado</InputLabel>
                        <Select value={plan.active === false ? "inactive" : "active"} label="Estado" onChange={event => updatePlan(index, "active", event.target.value === "active")}>
                          <MenuItem value="active">Activo</MenuItem>
                          <MenuItem value="inactive">Inactivo</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl variant="outlined" margin="dense">
                        <InputLabel>Destacado</InputLabel>
                        <Select value={plan.highlighted ? "yes" : "no"} label="Destacado" onChange={event => updatePlan(index, "highlighted", event.target.value === "yes")}>
                          <MenuItem value="no">No</MenuItem>
                          <MenuItem value="yes">Si</MenuItem>
                        </Select>
                      </FormControl>
                      <Button onClick={() => setForm(current => ({ ...current, plans: current.plans.filter((item, planIndex) => planIndex !== index) }))}>
                        <DeleteOutlineIcon />
                      </Button>
                    </div>
                  ))}
                  <Button startIcon={<AddIcon />} onClick={() => setForm(current => ({ ...current, plans: [...current.plans, emptyPlan] }))}>
                    Agregar plan
                  </Button>
                </div>
              )}

              {tab === "domains" && (
                <div className={classes.section}>
                  {form.domains.map((domain, index) => (
                    <div className={classes.domainRow} key={domain.id || index}>
                      <TextField variant="outlined" margin="dense" label="Dominio" value={domain.domain} onChange={event => updateDomain(index, "domain", event.target.value)} />
                      <Button onClick={() => setForm(current => ({ ...current, domains: current.domains.filter((item, domainIndex) => domainIndex !== index) }))}>
                        <DeleteOutlineIcon />
                      </Button>
                    </div>
                  ))}
                  <Button startIcon={<AddIcon />} onClick={() => setForm(current => ({ ...current, domains: [...current.domains, emptyDomain] }))}>
                    Agregar dominio
                  </Button>
                </div>
              )}

              {tab === "companies" && (
                <div className={`${classes.section} ${classes.companyList}`}>
                  {(form.companies || []).map(company => (
                    <div className={classes.companyItem} key={company.id}>
                      <Typography variant="subtitle1">{company.name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        Usuarios: {company.users?.length || 0}/{company.maxUsers || "-"} | Conexiones: {company.connections?.length || 0}/{company.maxConnections || "-"}
                      </Typography>
                      <div className={classes.companyActions}>
                        <Button
                          size="small"
                          color="primary"
                          variant="contained"
                          onClick={() => handleEnterAccount(company)}
                        >
                          Ingresar
                        </Button>
                        {!isSuperAdmin && (
                          <Button
                            size="small"
                            color="primary"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => openConnectionDialog(company)}
                          >
                            Agregar conexiones
                          </Button>
                        )}
                      </div>
                      {(company.connections || []).map(connection => {
                        const remainingDays = getRemainingDays(connection.expiresAt);

                        return (
                          <div key={connection.id} className={classes.connectionItem}>
                            <Typography variant="body2">
                              {connection.name || "Conexion"} - Vence: {formatDate(connection.expiresAt)}
                            </Typography>
                            <Chip
                              size="small"
                              color={remainingDays !== null && remainingDays < 0 ? "secondary" : "primary"}
                              label={formatRemainingDays(remainingDays)}
                            />
                            {!isSuperAdmin && (
                              <Button
                                size="small"
                                color="primary"
                                onClick={() => handleRenewConnection(company, connection)}
                              >
                                Renovar conexion
                              </Button>
                            )}
                            {isSuperAdmin && (
                              <Button
                                size="small"
                                color="primary"
                                onClick={() => handleEditConnectionExpiration(company, connection)}
                              >
                                Editar vencimiento
                              </Button>
                            )}
                          </div>
                        );
                      })}
                      {!company.connections?.length && (
                        <Typography variant="body2" color="textSecondary">
                          Esta cuenta aun no tiene conexiones para activar.
                        </Typography>
                      )}
                    </div>
                  ))}
                  {!form.companies?.length && (
                    <Typography variant="body2" color="textSecondary">
                      No hay cuentas creadas por este reseller.
                    </Typography>
                  )}
                </div>
              )}
            </form>
          )}
        </Paper>
      </div>

      <Dialog open={creditDialogOpen} onClose={() => setCreditDialogOpen(false)} maxWidth="xs" fullWidth>
        <form onSubmit={handleAddCredits}>
          <DialogTitle>Agregar creditos</DialogTitle>
          <DialogContent dividers>
            <TextField fullWidth type="number" variant="outlined" margin="dense" label="Cantidad" value={creditAmount} onChange={event => setCreditAmount(event.target.value)} />
            <TextField fullWidth multiline minRows={3} variant="outlined" margin="dense" label="Nota" value={creditNote} onChange={event => setCreditNote(event.target.value)} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreditDialogOpen(false)}>Cancelar</Button>
            <Button color="primary" variant="contained" type="submit">Guardar</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={companyDialogOpen} onClose={() => setCompanyDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleCreateCompany}>
          <DialogTitle>Crear cuenta del cliente</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth required variant="outlined" margin="dense" label="Empresa" value={companyForm.companyName} onChange={event => setCompanyForm(current => ({ ...current, companyName: event.target.value }))} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth required variant="outlined" margin="dense" label="Nombre admin" value={companyForm.name} onChange={event => setCompanyForm(current => ({ ...current, name: event.target.value }))} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth required variant="outlined" margin="dense" label="Correo admin" value={companyForm.email} onChange={event => setCompanyForm(current => ({ ...current, email: event.target.value }))} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth required type="password" variant="outlined" margin="dense" label="Password" value={companyForm.password} onChange={event => setCompanyForm(current => ({ ...current, password: event.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  La cuenta se creara con 1 usuario, 1 conexion y 30 dias gratis en la conexion inicial.
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCompanyDialogOpen(false)}>Cancelar</Button>
            <Button color="primary" variant="contained" type="submit">Crear cuenta</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={connectionDialogOpen} onClose={() => setConnectionDialogOpen(false)} maxWidth="xs" fullWidth>
        <form onSubmit={handleAddConnections}>
          <DialogTitle>Agregar conexiones</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="textSecondary">
              Se descontara 1 credito por cada conexion agregada.
            </Typography>
            <TextField
              fullWidth
              required
              type="number"
              variant="outlined"
              margin="dense"
              label="Cantidad"
              value={connectionQuantity}
              inputProps={{ min: 1, step: 1 }}
              onChange={event => setConnectionQuantity(event.target.value)}
            />
            <Typography variant="body2" color="textSecondary">
              Total a descontar: {Number(connectionQuantity || 0)} creditos.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConnectionDialogOpen(false)}>Cancelar</Button>
            <Button color="primary" variant="contained" type="submit">Agregar</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={renewDialogOpen} onClose={() => setRenewDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar renovacion</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Vas a renovar la conexion {renewTarget?.connection?.name || "Conexion"} de {renewTarget?.company?.name || "esta cuenta"}.
          </Typography>
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel>Periodo</InputLabel>
            <Select
              value={renewalMonths}
              label="Periodo"
              onChange={event => setRenewalMonths(Number(event.target.value))}
            >
              {renewalOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="textSecondary">
            Se agregaran {selectedRenewalOption.grantedMonths} meses y se descontaran {selectedRenewalOption.chargedMonths} creditos al confirmar.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenewDialogOpen(false)}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={confirmRenewConnection}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={expirationDialogOpen} onClose={closeExpirationDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Editar vencimiento</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary">
            Conexion {expirationTarget?.connection?.name || "Conexion"} de {expirationTarget?.company?.name || "esta cuenta"}.
          </Typography>
          <TextField
            fullWidth
            required
            type="date"
            variant="outlined"
            margin="dense"
            label="Fecha de vencimiento"
            value={expirationDate}
            InputLabelProps={{ shrink: true }}
            onChange={event => setExpirationDate(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeExpirationDialog}>Cancelar</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!expirationDate}
            onClick={confirmUpdateConnectionExpiration}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Reseller;
